'use client';

/**
 * Shared per-kind renderers for artifact content.
 *
 * Extracted out of ArtifactViewer because the CARD now renders some kinds
 * inline (image, chart, small table — see `artifactInlinePreview`). Two copies
 * of the chart or table renderer would drift, and the parse-failure fallback is
 * exactly the kind of detail that drifts silently.
 *
 * Every renderer degrades rather than throws: malformed content shows the raw
 * text in the code view. These parts are persisted jsonb that a future schema
 * change (or a hand edit) can invalidate, and a crash here takes the whole
 * message list with it.
 */

import React, { useMemo } from 'react';
import { ChartSpecSchema, renderChart } from '@west-tennessee-consulting/ui/components/charts/ChartRenderer';
import { MemoizedMarkdown } from '../MemoizedMarkdown.js';
import { HighlightedCode } from './HighlightedCode.js';
import { ArtifactImage } from './ArtifactImage.js';
import { ArtifactTable } from './ArtifactTable.js';
import type { ArtifactData } from '../../lib/artifacts/types.js';
import { parseArtifactTable } from './artifact-utils.js';

/**
 * iframe sandbox value for html artifacts. Kept as an exported constant so the
 * security test can assert the literal — `allow-same-origin` must NEVER appear.
 */
export const ARTIFACT_HTML_SANDBOX = 'allow-scripts';

export function CodeContent({ content, language }: { content: string; language?: string }) {
  return (
    <pre className="meridian-hljs bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre">
      <HighlightedCode code={content} language={language} />
    </pre>
  );
}

function ParseFallback({ what, content }: { what: string; content: string }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground italic">
        Couldn&apos;t parse {what} — showing raw content.
      </p>
      <CodeContent content={content} language="json" />
    </div>
  );
}

export interface TableContentProps {
  content: string;
  variant?: 'preview' | 'full';
  /** Called by the preview footer when the reader wants the whole table. */
  onExpand?: () => void;
}

export function TableContent({ content, variant = 'full', onExpand }: TableContentProps) {
  const parsed = useMemo(() => parseArtifactTable(content), [content]);

  if (!parsed) {
    return <ParseFallback what="table data" content={content} />;
  }

  return <ArtifactTable table={parsed} variant={variant} onExpand={onExpand} />;
}

export function ChartContent({ content }: { content: string }) {
  const chart = useMemo(() => {
    try {
      const parsed = ChartSpecSchema.safeParse(JSON.parse(content));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }, [content]);

  if (!chart) {
    return <ParseFallback what="chart data" content={content} />;
  }

  return <div className="w-full">{renderChart(chart)}</div>;
}

/** The full-size rendering of an artifact, by kind. Used by the viewer. */
export function ArtifactContent({ artifact }: { artifact: ArtifactData }) {
  switch (artifact.kind) {
    case 'markdown':
      return (
        // MemoizedMarkdown owns markdown typography; `prose` is a no-op here
        // (typography plugin not loaded) apart from overriding its link color.
        <div className="max-w-none">
          <MemoizedMarkdown id={`artifact-${artifact.id}`} content={artifact.content} />
        </div>
      );
    case 'code':
      return <CodeContent content={artifact.content} language={artifact.language} />;
    case 'html':
      return (
        <div className="space-y-1.5">
          <iframe
            title={artifact.title}
            // SECURITY: allow-scripts ONLY — never allow-same-origin (untrusted
            // model HTML must not reach the app origin). See module doc.
            sandbox={ARTIFACT_HTML_SANDBOX}
            srcDoc={artifact.content}
            className="w-full h-[60vh] max-h-[70vh] rounded-md border border-border bg-white"
          />
          <p className="text-xs text-muted-foreground">
            Rendered in an isolated sandbox — no access to your Hub session.
          </p>
        </div>
      );
    case 'table':
      return <TableContent content={artifact.content} />;
    case 'chart':
      return <ChartContent content={artifact.content} />;
    case 'image':
      return (
        <div className="flex justify-center">
          <ArtifactImage artifact={artifact} variant="full" />
        </div>
      );
  }
}

/**
 * The in-flow rendering for an artifact the card shows inline. Returns null for
 * kinds that stay collapsed, so the card can branch on one call.
 *
 * Deliberately NOT the html kind: inlining it would start a sandboxed iframe in
 * the middle of the transcript without the user asking.
 */
export function ArtifactInlineContent({
  artifact,
  onExpand,
}: {
  artifact: ArtifactData;
  /** Opens the full viewer — the preview table's "show all rows" footer uses it. */
  onExpand?: () => void;
}) {
  switch (artifact.kind) {
    case 'image':
      return <ArtifactImage artifact={artifact} variant="preview" />;
    case 'chart':
      return <ChartContent content={artifact.content} />;
    case 'table':
      return <TableContent content={artifact.content} variant="preview" onExpand={onExpand} />;
    default:
      return null;
  }
}
